import { getUserTimeZone } from 'shared/utils/common';
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { RejectedValue, ErrorResponse, MainResponse } from "shared/interfaces/core-interfaces";
import { StartSessionRequest, StopSessionRequest, BreakSessionRequest, UpdateSessionRequest, InsertSessionRequest, MonthlyTrackingResponse, FullMonthlyRecapResponse, initialTimeTrackingState, MonthlyOverviewRequest, StateResponse, CreateCostCenterTrackingRequest, UpdateCostCenterTrackingRequest, GetCostCenterTrackingResponse, GetLatestCostCenterTrackingResponse, GetCostCenterTrackingProjectDateResponse, CreatePhaseRequest, UpdatePhaseRequest, GetPhaseResponse, TimeTrackingDataResponse, WorkSessionResponse, TimeTrackingState, MonthClosingResponse, GetMonthClosingsRequest, CreateMonthClosingRequest, CreateMonthReopenRequestRequest, FilterDataResponse, ProjectPlanningResponse } from "./timeTrackingInterfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { SessionType } from "constants/enums";
import { API_BASE_URL as baseUri } from "config/env";

/**
 * Reads a 200 whose payload is legitimately nullable.
 *
 * The endpoints behind these are `Results.Ok(result)` over a nullable result, and ASP.NET writes
 * *no body at all* for a null value — a 200 with content-length 0, not the literal `null`.
 * `response.json()` throws on that, which turns "this project has no planning template" into a
 * rejected thunk, so read the body as text and treat an empty one as the null it stands for.
 */
export async function readNullableJson<T>(response: Response): Promise<T | null> {
    const body = await response.text();
    return body ? (JSON.parse(body) as T) : null;
}

export const GetTimeTrackingData = createAsyncThunk<TimeTrackingDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetTimeTrackingData',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-tracking/data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TimeTrackingDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetFilterData = createAsyncThunk<FilterDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetFilterData',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/month-closing/filter-data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FilterDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

type ViewedContextState = {
    timeTrackingSlice: TimeTrackingState;
    authentificationSlice: { userSettings: { employeeId: string } | null; logTenantId: { toString(): string } | null };
};

/**
 * Whether the session-assignments view currently shows the logged-in user's own data for the
 * current month — the only case in which the widget's start/stop/break may re-read it.
 *
 * Those actions always act on the caller's own session, and the refresh they trigger carries no
 * employee, so the backend answers with the caller's month. Doing that while a colleague was
 * filtered in swapped their sessions for the caller's own but left the employee filter showing
 * the colleague, so the view claimed to be theirs while listing someone else's time.
 */
function isViewingOwnCurrentMonth(state: ViewedContextState): boolean {
    const { viewedMonth, viewedEmployeeId, viewedTenantId } = state.timeTrackingSlice;
    const now = new Date();
    if (!viewedMonth || viewedMonth.month !== now.getMonth() + 1 || viewedMonth.year !== now.getFullYear()) {
        return false;
    }
    const ownEmployeeId = state.authentificationSlice.userSettings?.employeeId;
    const logTenantId = state.authentificationSlice.logTenantId?.toString();
    // A null viewed employee/tenant means no filter is applied, which is already the own-data view.
    return (!viewedEmployeeId || viewedEmployeeId === ownEmployeeId)
        && (!viewedTenantId || !logTenantId || viewedTenantId === logTenantId);
}

export const StartSession = createAsyncThunk<MainResponse, StartSessionRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/StartSession',
    async (request, { rejectWithValue, dispatch, getState }) => {

        const response = await fetch(baseUri + 'api/time-tracking/start', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetSessionState({ timeZone: getUserTimeZone() }));
            if (isViewingOwnCurrentMonth(getState() as ViewedContextState)) {
                dispatch(GetMonthlySessions({ timeZone: getUserTimeZone() }));
                dispatch(GetFullMonthlyRecap({ timeZone: getUserTimeZone() }));
            }
            dispatch(showNotification({ message: "Session started successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const StopSession = createAsyncThunk<MainResponse, StopSessionRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/StopSession',
    async (request, { rejectWithValue, dispatch, getState }) => {

        const response = await fetch(baseUri + 'api/time-tracking/stop', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetSessionState({ timeZone: getUserTimeZone() }));
            if (isViewingOwnCurrentMonth(getState() as ViewedContextState)) {
                dispatch(GetMonthlySessions({ timeZone: getUserTimeZone() }));
                dispatch(GetFullMonthlyRecap({ timeZone: getUserTimeZone() }));
            }
            dispatch(showNotification({ message: "Session stopped successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const BreakSession = createAsyncThunk<MainResponse, BreakSessionRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/BreakSession',
    async (request, { rejectWithValue, dispatch, getState }) => {

        const response = await fetch(baseUri + 'api/time-tracking/break', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetSessionState({ timeZone: getUserTimeZone() }));
            if (isViewingOwnCurrentMonth(getState() as ViewedContextState)) {
                dispatch(GetMonthlySessions({ timeZone: getUserTimeZone() }));
                dispatch(GetFullMonthlyRecap({ timeZone: getUserTimeZone() }));
            }
            dispatch(showNotification({ message: "Break started successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeactivateSession = createAsyncThunk<MainResponse, { id: string; employeeId?: string; tenantId?: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/DeactivateSession',
    async (request, { rejectWithValue, dispatch, getState }) => {

        const response = await fetch(baseUri + 'api/time-tracking/deactivate/' + request.id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            const viewedMonth = (getState() as { timeTrackingSlice: TimeTrackingState }).timeTrackingSlice.viewedMonth;
            if (viewedMonth) {
                const monthRequest = { timeZone: getUserTimeZone(), month: viewedMonth.month, year: viewedMonth.year, employeeId: request.employeeId, tenantId: request.tenantId };
                dispatch(GetMonthlySessions(monthRequest));
                dispatch(GetFullMonthlyRecap(monthRequest));
            }
            dispatch(showNotification({ message: "Session deactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateSession = createAsyncThunk<MainResponse, UpdateSessionRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/UpdateSession',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/time-tracking/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            const date = new Date(request.startLocal);
            const monthRequest = { timeZone: getUserTimeZone(), employeeId: request.employeeId, tenantId: request.tenantId, month: date.getMonth() + 1, year: date.getFullYear() };
            dispatch(GetMonthlySessions(monthRequest));
            dispatch(GetFullMonthlyRecap(monthRequest));
            dispatch(showNotification({ message: "Session updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const InsertSession = createAsyncThunk<MainResponse, InsertSessionRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/InsertSession',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/time-tracking/insert', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            const date = new Date(request.startLocal);
            const monthRequest = { timeZone: getUserTimeZone(), employeeId: request.employeeId, tenantId: request.tenantId, month: date.getMonth() + 1, year: date.getFullYear() };
            dispatch(GetMonthlySessions(monthRequest));
            dispatch(GetFullMonthlyRecap(monthRequest));
            dispatch(showNotification({ message: "Session inserted successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetMonthlySessions = createAsyncThunk<MonthlyTrackingResponse, MonthlyOverviewRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetMonthlySessions',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/time-tracking/monthly', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MonthlyTrackingResponse;
            dispatch(GetCostCenterTrackingsByProjectDuration({
                date: new Date(request.year ?? new Date().getFullYear(), (request.month ? (request.month - 1) : new Date().getMonth()), 5),
                employeeId: request.employeeId, tenantId: request.tenantId
            }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetFullMonthlyRecap = createAsyncThunk<FullMonthlyRecapResponse, MonthlyOverviewRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetFullMonthlyRecap',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/time-tracking/full-recap', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as FullMonthlyRecapResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetSessionState = createAsyncThunk<StateResponse, { timeZone: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetSessionState',
    async ({ timeZone }, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-tracking/state', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({ timeZone })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as StateResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateCostCenterTracking = createAsyncThunk<GetCostCenterTrackingResponse, CreateCostCenterTrackingRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/CreateCostCenterTracking',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/costcentertracking/createcostcentertracking', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetCostCenterTrackingResponse;
            dispatch(GetCostCenterTrackingsByProjectDuration({ date: new Date(request.date) }));
            dispatch(showNotification({ message: "Cost center tracking created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateCostCenterTracking = createAsyncThunk<GetCostCenterTrackingResponse, UpdateCostCenterTrackingRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/UpdateCostCenterTracking',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/costcentertracking/updatecostcentertracking', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetCostCenterTrackingResponse;
            dispatch(GetCostCenterTrackingsByProjectDuration({ date: new Date(request.date) }));
            dispatch(showNotification({ message: "Cost center tracking updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteCostCenterTracking = createAsyncThunk<MainResponse, { id: string, date: Date }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/DeleteCostCenterTracking',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/costcentertracking/deletecostcentertracking?id=' + request.id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetAllCostCenterTrackingsByEmployeeAndDate({ date: new Date(request.date) }));
            dispatch(GetCostCenterTrackingsByProjectDuration({ date: new Date(request.date) }));
            dispatch(showNotification({ message: "Cost center tracking deleted successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllCostCenterTrackingsByEmployeeAndDate = createAsyncThunk<GetCostCenterTrackingResponse[], { employeeId?: string; date: Date }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetAllCostCenterTrackingsByEmployeeAndDate',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/costcentertracking/getallcostcentertrackingsbyemployeeanddate', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetCostCenterTrackingResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCostCenterTrackingsByProjectDuration = createAsyncThunk<Record<string, GetCostCenterTrackingProjectDateResponse>, { date: Date; employeeId?: string; tenantId?: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetCostCenterTrackingsByProjectDuration',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/costcentertracking/getcostcentertrackingsbyprojectduration', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Record<string, GetCostCenterTrackingProjectDateResponse>;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetLatestCostCenterTrackingByProject = createAsyncThunk<GetLatestCostCenterTrackingResponse | null, { projectId: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetLatestCostCenterTrackingByProject',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/costcentertracking/getlatestcostcentertrackingbyproject', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            // Nothing tracked on this project yet — nothing to prefill from.
            const responseModel = await readNullableJson<GetLatestCostCenterTrackingResponse>(response);
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreatePhase = createAsyncThunk<MainResponse, CreatePhaseRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/CreatePhase',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/phase/createphase', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: "Phase created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdatePhase = createAsyncThunk<MainResponse, UpdatePhaseRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/UpdatePhase',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/phase/updatephase', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: "Phase updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeletePhase = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/DeletePhase',
    async (phaseId, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + `api/phase/deletephase?id=${phaseId}`, {
            method: "POST",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: "Phase deleted successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPhaseById = createAsyncThunk<GetPhaseResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetPhaseById',
    async (id, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + `api/phase/getphasebyid?phaseId=${id}`, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPhaseResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPhasesByTenantId = createAsyncThunk<GetPhaseResponse[], string, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetPhasesByTenantId',
    async (tenantId, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + `api/phase/getphasebytenantid?tenantId=${tenantId}`, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPhaseResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPhasesByParentPhaseId = createAsyncThunk<GetPhaseResponse[], string, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetPhasesByParentPhaseId',
    async (parentPhaseId, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + `api/phase/getphasesbyparentphaseid?parentPhaseId=${parentPhaseId}`, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPhaseResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPhasesByProjectId = createAsyncThunk<GetPhaseResponse[], string, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetPhasesByProjectId',
    async (projectId, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + `api/phase/getphasesbyprojectid?projectId=${projectId}`, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPhaseResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// #region MonthClosing

export const GetMonthClosings = createAsyncThunk<MonthClosingResponse[], GetMonthClosingsRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetMonthClosings',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/month-closing/get', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MonthClosingResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateMonthClosing = createAsyncThunk<MainResponse, CreateMonthClosingRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/CreateMonthClosing',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/month-closing/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetMonthClosings({ employeeId: request.employeeId, tenantId: request.tenantId }));
            dispatch(showNotification({ message: responseModel.message ?? "Month closing created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CloseMonth = createAsyncThunk<MainResponse, { id: string, employeeId: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/CloseMonth',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/month-closing/close/' + request.id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetMonthClosings({ employeeId: request.employeeId }));
            dispatch(showNotification({ message: responseModel.message ?? "Month closed successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const RequestReopenMonth = createAsyncThunk<MainResponse, CreateMonthReopenRequestRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/RequestReopenMonth',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/month-closing/request-reopen', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetMonthClosings({ employeeId: request.employeeId }));
            dispatch(showNotification({ message: responseModel.message ?? "Month reopening request created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// #endregion

export const UpdateSessionLocation = createAsyncThunk<void, { locationId: number }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/UpdateSessionLocation',
    async ({ locationId }, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-tracking/settings/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({ locationId })
        });
        if (response.status === 200) {
            dispatch(showNotification({ message: "Session state updated successfully.", type: NotificationTypes.Success }));
            return;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateSessionProject = createAsyncThunk<void, { projectId: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/UpdateSessionProject',
    async ({ projectId }, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-tracking/settings/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({ projectId })
        });
        if (response.status === 200) {
            dispatch(showNotification({ message: "Session state updated successfully.", type: NotificationTypes.Success }));
            return;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCostCenterPlanning = createAsyncThunk<ProjectPlanningResponse | null, string, { rejectValue: RejectedValue }>(
    'Slice/TimeTrackingSlice/GetCostCenterPlanning',
    async (projectId: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/get-by-project?projectId=' + projectId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            // No template for this project is an answer, not a failure: the tracking then saves
            // without phases. Only a real error may reject, or Save stays disabled forever.
            const responseModel = await readNullableJson<ProjectPlanningResponse>(response);
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const TimeTrackingSlice = createSlice({
    name: 'TimeTrackingSlice',
    initialState: initialTimeTrackingState,
    reducers: {
        setCurrentState: (state, action: PayloadAction<StateResponse>) => {
            if (state.sessionState) {
                state.sessionState = { ...state.sessionState, ...action.payload };
            }
        },
        setHasSessionState: (state, action: PayloadAction<boolean>) => {
            if (state.sessionState) {
                state.hasSessionState = action.payload;
            }
        },
        setViewedContext: (state, action: PayloadAction<{ month: number; year: number; employeeId: string | null; tenantId: string | null }>) => {
            state.viewedMonth = { month: action.payload.month, year: action.payload.year };
            state.viewedEmployeeId = action.payload.employeeId;
            state.viewedTenantId = action.payload.tenantId;
        },
        clearViewedContext: (state) => {
            state.viewedMonth = null;
            state.viewedEmployeeId = null;
            state.viewedTenantId = null;
        },
        clearSessionState: (state) => {
            state.sessionState = null;
            state.hasSessionState = false;
        },
        resetProjectPlanning: (state) => {
            state.projectPlanning = null;
            state.projectPlanningRequestId = null;
            state.projectPlanningProjectId = null;
            state.projectPlanningStatus = "idle";
        }
    },
    extraReducers(builder) {
        builder.addCase(GetTimeTrackingData.fulfilled, (state, action: PayloadAction<TimeTrackingDataResponse>) => {
            state.data = {
                ...action.payload,
                projects: [...action.payload.projects].sort((a, b) => a.label.localeCompare(b.label)),
            };
        });
        builder.addCase(GetFilterData.fulfilled, (state, action: PayloadAction<FilterDataResponse>) => {
            state.filterData = action.payload;
        });
        builder.addCase(DeactivateSession.fulfilled, (state, action) => {
            if (state.monthlySession) {
                for (const [key, day] of Object.entries(state.monthlySession.dailyOverview)) {
                    const sessions = day.sessions.filter(s => 'id' in s && s.id !== action.meta.arg.id);
                    state.monthlySession.dailyOverview[key].sessions = sessions;
                }
            }
        });
        builder.addCase(GetMonthlySessions.fulfilled, (state, action: PayloadAction<MonthlyTrackingResponse>) => {
            state.monthlySession = action.payload;
        });
        builder.addCase(GetFullMonthlyRecap.fulfilled, (state, action: PayloadAction<FullMonthlyRecapResponse>) => {
            state.monthlyRecap = action.payload;
        });
        builder.addCase(GetSessionState.fulfilled, (state, action: PayloadAction<StateResponse>) => {
            state.sessionState = action.payload;
            const rememberMe = JSON.parse(localStorage.getItem('rememberMe') as string);
            if (rememberMe) {
                localStorage.setItem('HubToken', action.payload.hubToken);
            }
            else {
                sessionStorage.setItem('HubToken', action.payload.hubToken);
            }
        });
        builder.addCase(UpdateSessionLocation.fulfilled, (state, action) => {
            if (state.sessionState?.settings) {
                state.sessionState.settings.locationId = action.meta.arg.locationId;
                state.sessionState.settings.location = state.data?.locations.find(l => l.value === action.meta.arg.locationId)?.label ?? state.sessionState.settings.location;
            }
        });
        builder.addCase(UpdateSessionProject.fulfilled, (state, action) => {
            if (state.sessionState?.settings) {
                state.sessionState.settings.projectId = action.meta.arg.projectId;
                state.sessionState.settings.project = state.data?.projects.find(p => p.value === action.meta.arg.projectId)?.label ?? state.sessionState.settings.project;
            }
        });
        builder.addCase(CreateCostCenterTracking.fulfilled, (state, action: PayloadAction<GetCostCenterTrackingResponse>) => {
            const cct = action.payload as GetCostCenterTrackingResponse;
            if (!state.monthlySession) return;

            for (const [key, day] of Object.entries(state.monthlySession.dailyOverview)) {
                const updatedSessions = day.sessions.map(session => {
                    if (session.type === SessionType.Work || session.type === SessionType.Travel) {
                        const workSession = session as WorkSessionResponse;
                        if (workSession.id === cct.timeTrackingSessionId) {
                            const existing = workSession.costCenterTrackings ?? [];
                            const newCcts = [...existing, cct];
                            return { ...workSession, costCenterTrackings: newCcts } as WorkSessionResponse;
                        }
                    }
                    return session;
                });

                state.monthlySession.dailyOverview[key].sessions = updatedSessions;
            }

        });
        builder.addCase(UpdateCostCenterTracking.fulfilled, (state, action: PayloadAction<GetCostCenterTrackingResponse>) => {
            const cct = action.payload as GetCostCenterTrackingResponse;
            if (!state.monthlySession) return;

            for (const [key, day] of Object.entries(state.monthlySession.dailyOverview)) {
                const updatedSessions = day.sessions.map(session => {
                    if (session.type === SessionType.Work || session.type === SessionType.Travel) {
                        const workSession = session as WorkSessionResponse;
                        if (workSession.id === cct.timeTrackingSessionId) {
                            const existing = workSession.costCenterTrackings ?? [];
                            const newCcts = existing.map(e => e.costCenterTrackingId === cct.costCenterTrackingId ? cct : e);
                            return { ...workSession, costCenterTrackings: newCcts } as WorkSessionResponse;
                        }
                    }
                    return session;
                });

                state.monthlySession.dailyOverview[key].sessions = updatedSessions;
            }

        });
        builder.addCase(GetAllCostCenterTrackingsByEmployeeAndDate.fulfilled, (state, action) => {
            const dateKey = action.meta.arg.date.toISOString().replace('.000', '');
            if (!state.monthlySession) return;
            const day = state.monthlySession.dailyOverview[dateKey];
            if (!day) return;

            const trackings = action.payload as GetCostCenterTrackingResponse[];

            const updatedSessions = day.sessions.map(session => {
                if (session.type === SessionType.Work || session.type === SessionType.Travel) {
                    const workSession = session as WorkSessionResponse;
                    const ccts = trackings.filter(cct => cct.timeTrackingSessionId === workSession.id);
                    return { ...workSession, costCenterTrackings: ccts } as WorkSessionResponse;
                }
                return session;
            });

            state.monthlySession.dailyOverview[dateKey].sessions = updatedSessions;
        });
        builder.addCase(GetCostCenterTrackingsByProjectDuration.fulfilled, (state, action) => {
            if (!state.monthlySession) return;
            state.monthlySession.costCentersThisMonth = action.payload;
        });
        builder.addCase(GetMonthClosings.fulfilled, (state, action: PayloadAction<MonthClosingResponse[]>) => {
            state.monthClosings = action.payload;
        });
        builder.addCase(GetCostCenterPlanning.pending, (state, action) => {
            // Clear immediately so the previous cost center's phases are never shown while loading
            state.projectPlanning = null;
            state.projectPlanningRequestId = action.meta.requestId;
            state.projectPlanningProjectId = action.meta.arg;
            state.projectPlanningStatus = "loading";
        });
        builder.addCase(GetCostCenterPlanning.fulfilled, (state, action) => {
            if (state.projectPlanningRequestId === action.meta.requestId) {
                state.projectPlanning = action.payload;
                // A null payload is an answer too: this project has no planning template.
                state.projectPlanningStatus = "loaded";
            }
        });
        builder.addCase(GetCostCenterPlanning.rejected, (state, action) => {
            if (state.projectPlanningRequestId === action.meta.requestId) {
                state.projectPlanning = null;
                state.projectPlanningStatus = "failed";
            }
        });
    }
});

export const { setCurrentState, setHasSessionState, setViewedContext, clearViewedContext, clearSessionState, resetProjectPlanning } = TimeTrackingSlice.actions;
export default TimeTrackingSlice.reducer;
